import { Icon } from '@ds/core';

import type { PendingAction } from './acaoPendente';
import type { InteracaoDaVoz } from './useAcoesDaVoz';

/**
 * `VoiceInteractionOverlay` (B9, mockup do usuário em 13/09/2026): a decisão aparece SOBRE a tela
 * de voz, como uma pergunta dentro da conversa — nunca como outra tela. É só camada: a sessão, o
 * orbe e a fala continuam embaixo, sem recriar nada.
 *
 * - aguardando: véu escuro leve sobre a tela, cartão embaixo com ícone, dados com ícone, a
 *   pergunta e os dois botões lado a lado (`ConfirmationCard`);
 * - executando: o mesmo cartão, com o andamento no lugar dos botões;
 * - desfecho: cartão compacto — "Pronto!", "Tudo bem" ou "Não foi feito" — com Continuar, e o
 *   véu some (`ExecutionStatus`).
 *
 * Escolha entre opções (`ChoiceCard`) e prévias mais ricas entram por aqui quando chegarem.
 */

export interface SobreposicaoDaVozProps {
    interacao: InteracaoDaVoz | null;
    somenteLeitura?: string | null;
    aoConfirmar: () => void;
    aoCancelar: () => void;
    aoDispensar: () => void;
}

export function SobreposicaoDaVoz({ interacao, somenteLeitura, aoConfirmar, aoCancelar, aoDispensar }: SobreposicaoDaVozProps) {
    if (!interacao?.visivel) return null;
    const desfecho = interacao.estado === 'feita' || interacao.estado === 'cancelada' || interacao.estado === 'falhou';
    return (
        <div className={`brain-vozmodo__sobreposicao${desfecho ? ' brain-vozmodo__sobreposicao--desfecho' : ''}${interacao.saindo ? ' brain-vozmodo__sobreposicao--saindo' : ''}`}>
            {!desfecho && <div className="brain-vozmodo__veu" aria-hidden="true" />}
            {desfecho
                ? <CartaoDeDesfecho interacao={interacao} aoContinuar={aoDispensar} />
                : <CartaoDeConfirmacao interacao={interacao} somenteLeitura={somenteLeitura ?? null} aoConfirmar={aoConfirmar} aoCancelar={aoCancelar} />}
        </div>
    );
}

/* ------------------------------------------------------------------ ícones */

const ICONE_DA_ACAO: Record<string, string> = {
    propor_agendamento: 'calendar-plus',
    propor_remarcar: 'calendar-clock',
    propor_cancelar_agendamento: 'calendar-x',
    propor_tarefa: 'list-checks',
    propor_lead: 'user-plus',
    propor_mover_lead: 'move-right',
    propor_pagamento: 'banknote',
    propor_procedimento: 'sparkles',
    propor_produto: 'package-plus',
    propor_categoria_produto: 'folder-plus',
    propor_movimentacao_estoque: 'arrow-left-right',
    propor_contagem_estoque: 'clipboard-check',
    propor_titulo: 'receipt',
    propor_editar_titulo: 'file-pen',
    propor_transferencia: 'arrow-right-left',
    propor_conta_financeira: 'landmark',
    propor_categoria_financeira: 'tags',
    propor_forma_de_pagamento: 'credit-card',
    propor_situacao_tarefa: 'circle-check',
    propor_situacao_agendamento: 'calendar-check',
    propor_paciente: 'user-plus',
    propor_mensagem: 'message-circle',
};

const PERGUNTA_DA_ACAO: Record<string, string> = {
    propor_agendamento: 'Deseja confirmar este agendamento?',
    propor_remarcar: 'Deseja remarcar este agendamento?',
    propor_cancelar_agendamento: 'Deseja cancelar este agendamento? Não dá para desfazer.',
    propor_tarefa: 'Deseja criar esta tarefa?',
    propor_lead: 'Deseja criar este lead?',
    propor_mover_lead: 'Deseja mover este lead?',
    propor_pagamento: 'Deseja registrar este pagamento?',
    propor_procedimento: 'Deseja salvar este procedimento?',
    propor_produto: 'Deseja salvar este produto?',
    propor_categoria_produto: 'Deseja criar esta categoria?',
    propor_movimentacao_estoque: 'Deseja registrar esta movimentação?',
    propor_contagem_estoque: 'Deseja ajustar o saldo pela contagem?',
    propor_titulo: 'Deseja criar este lançamento?',
    propor_editar_titulo: 'Deseja editar este lançamento?',
    propor_transferencia: 'Deseja fazer esta transferência?',
    propor_conta_financeira: 'Deseja salvar esta conta?',
    propor_categoria_financeira: 'Deseja criar esta categoria de conta?',
    propor_forma_de_pagamento: 'Deseja criar esta forma de pagamento?',
    propor_situacao_tarefa: 'Deseja atualizar esta tarefa?',
    propor_situacao_agendamento: 'Deseja atualizar este agendamento?',
    propor_paciente: 'Deseja cadastrar este paciente?',
    propor_mensagem: 'Deseja enviar esta mensagem?',
};

/* O ícone diz o que é o dado; o rótulo continua para leitor de tela e quando o ícone não sabe. */
const ICONES_DOS_CAMPOS: Array<[RegExp, string]> = [
    [/paciente|cliente|contato|lead|nome/i, 'user'],
    [/quando|data|hora|hor[aá]rio|dia/i, 'clock'],
    [/profissional|dr\.?|dra\.?|m[eé]dic/i, 'stethoscope'],
    [/procedimento|servi[cç]o/i, 'sparkles'],
    [/sala|unidade|local/i, 'map-pin'],
    [/valor|pre[cç]o|pagamento|forma/i, 'banknote'],
    [/canal|telefone|whatsapp|para|destinat/i, 'send'],
    [/etapa|funil|pipeline/i, 'git-branch'],
];

function iconeDoCampo(rotulo: string): string | null {
    return ICONES_DOS_CAMPOS.find(([padrao]) => padrao.test(rotulo))?.[1] ?? null;
}

/* ------------------------------------------------------------------ confirmação */

interface CartaoDeConfirmacaoProps {
    interacao: InteracaoDaVoz;
    somenteLeitura: string | null;
    aoConfirmar: () => void;
    aoCancelar: () => void;
}

/** `ConfirmationCard`: os dados exatos da ação (regra 8), a pergunta e a decisão. */
function CartaoDeConfirmacao({ interacao, somenteLeitura, aoConfirmar, aoCancelar }: CartaoDeConfirmacaoProps) {
    const { acao, estado } = interacao;
    const risco = acao.risk ?? 'normal';
    const executando = estado === 'executando';
    return (
        <section className={`brain-vozcartao brain-vozcartao--${estado} brain-vozcartao--risco-${risco}`} aria-label={`Confirmação: ${acao.title}`}>
            <div className="brain-vozcartao__topo">
                <span className="brain-vozcartao__icone" aria-hidden="true"><Icon name={ICONE_DA_ACAO[acao.type] ?? 'sparkles'} size={18} /></span>
                <h2 className="brain-vozcartao__titulo">{acao.title}</h2>
                {interacao.total > 1 && <span className="brain-vozcartao__fila">{interacao.posicao} de {interacao.total}</span>}
                {!executando && !somenteLeitura && (
                    <button type="button" className="brain-vozcartao__fechar" onClick={aoCancelar} aria-label="Fechar sem fazer">
                        <Icon name="x" size={16} />
                    </button>
                )}
            </div>
            {acao.details && acao.details.length > 0 && (
                <dl className="brain-vozcartao__dados">
                    {acao.details.map((d, i) => {
                        const icone = iconeDoCampo(d.label);
                        return (
                            /* Rótulos podem se repetir; a ordem é a do servidor. */
                            <div key={`${d.label}-${String(i)}`} className="brain-vozcartao__dado">
                                <dt>
                                    {icone ? <><Icon name={icone} size={16} /><span className="brain-vozcartao__oculto">{d.label}</span></> : <span className="brain-vozcartao__rotulo">{d.label}</span>}
                                </dt>
                                <dd>{d.value}</dd>
                            </div>
                        );
                    })}
                </dl>
            )}
            {acao.preview && <PreviaDaAcao previa={acao.preview} />}
            {somenteLeitura ? (
                <p className="brain-vozcartao__pergunta">{somenteLeitura}</p>
            ) : executando ? (
                <div className="brain-vozcartao__andamento" role="status">
                    <span className="brain-vozcartao__giro" aria-hidden="true"><Icon name="loader-circle" size={18} /></span>
                    {interacao.decisao === 'cancelar' ? 'Cancelando…' : 'Executando…'}
                </div>
            ) : (
                <>
                    <p className="brain-vozcartao__pergunta">{PERGUNTA_DA_ACAO[acao.type] ?? 'Deseja confirmar?'}</p>
                    <div className="brain-vozcartao__botoes">
                        <button type="button" className="brain-vozcartao__botao brain-vozcartao__botao--secundario" onClick={aoCancelar}>
                            {acao.cancelLabel ?? 'Agora não'}
                        </button>
                        <button type="button" className="brain-vozcartao__botao brain-vozcartao__botao--primario" onClick={aoConfirmar}>
                            {acao.confirmLabel ?? 'Confirmar'}
                        </button>
                    </div>
                </>
            )}
        </section>
    );
}

/* ------------------------------------------------------------------ desfecho */

const DESFECHO: Record<'feita' | 'cancelada' | 'falhou', { titulo: string; icone: string }> = {
    feita: { titulo: 'Pronto!', icone: 'check' },
    cancelada: { titulo: 'Tudo bem', icone: 'x' },
    falhou: { titulo: 'Não foi feito', icone: 'circle-alert' },
};

/** `ExecutionStatus`: o desfecho curto, com Continuar para seguir a conversa na hora. */
function CartaoDeDesfecho({ interacao, aoContinuar }: { interacao: InteracaoDaVoz; aoContinuar: () => void }) {
    const estado = interacao.estado === 'cancelada' || interacao.estado === 'falhou' ? interacao.estado : 'feita';
    const { titulo, icone } = DESFECHO[estado];
    return (
        <section className={`brain-vozcartao brain-vozcartao--desfecho brain-vozcartao--${estado}`} aria-label={`Desfecho: ${interacao.acao.title}`}>
            <div className="brain-vozcartao__resultado" role="status">
                <span className="brain-vozcartao__selo" aria-hidden="true"><Icon name={icone} size={20} /></span>
                <strong>{titulo}</strong>
                {interacao.mensagem && <span>{interacao.mensagem}</span>}
            </div>
            <button type="button" className="brain-vozcartao__botao brain-vozcartao__botao--primario brain-vozcartao__botao--largo" onClick={aoContinuar}>
                Continuar
            </button>
        </section>
    );
}

function PreviaDaAcao({ previa }: { previa: NonNullable<PendingAction['preview']> }) {
    if (previa.type === 'message' && typeof previa.content === 'string') {
        return <blockquote className="brain-vozcartao__previa brain-vozcartao__previa--mensagem">{previa.content}</blockquote>;
    }
    if (!previa.title && !previa.summary) return null;
    return (
        <div className="brain-vozcartao__previa">
            {previa.title && <strong>{previa.title}</strong>}
            {previa.summary && <p>{previa.summary}</p>}
        </div>
    );
}
